import "react-quill/dist/quill.snow.css";
import ReactQuill from "react-quill";
import { useEffect, useMemo, useRef, useState } from "react";
import { unescape } from "lodash";
import Quill from "quill";

const Delta = Quill.import("delta");

const Editor = ({
  placeholder,
  value,
  handleChange,
  error,
  touched,
  isDisabled,
  maxHeight,
  maxLength,
}: {
  placeholder: string;
  value: string;
  handleChange: (e: any) => void;
  error?: string;
  touched?: boolean;
  isDisabled?: boolean;
  maxHeight?: string;
  maxLength?: number;
}) => {
  const [content, setContent] = useState("");
  const reactQuillRef = useRef<ReactQuill>();
  const [remainingCharacters, setRemainingCharacters] = useState(maxLength);

  const handleOnChange = (e, delta, source, editor) => {
    // const unprivilegedEditor = reactQuillRef?.current?.getEditor();
    // const plainText = unprivilegedEditor?.getText().replace(/\n/g, "");
    // const textLength = plainText.length;

    // If the text exceeds maxLength, trim it
    // if (textLength > maxLength) {
    //   const trimmedContent = plainText.substring(0, maxLength);

    //   // Trim Delta to max length
    //   const trimmedDelta = trimDeltaToMaxLength(
    //     editor.getContents(),
    //     maxLength
    //   );
    //   // setContent(trimmedContent);
    //   // handleChange(trimmedContent);

    //   // Replace editor's content with trimmed content
    //   // unprivilegedEditor.setText(trimmedContent);
    //   unprivilegedEditor.setContents(trimmedDelta);
    // } else {
    setContent(e);
    handleChange(e);
    // }
  };

  // Prevent any input except Backspace
  // const handleOnKeyDown = (event) => {
  //   const unprivilegedEditor = reactQuillRef?.current?.getEditor();
  //   const plainText = unprivilegedEditor?.getText().replace(/\n/g, "");
  //   const textLength = plainText.length;
  //   if (textLength >= maxLength && event.key !== "Backspace") {
  //     event.preventDefault(); // Block further input
  //   }
  // };

  useEffect(() => {
    // const decodeHTML = (html) => {
    //   const txt = document.createElement("textarea");
    //   txt.innerHTML = html;
    //   return txt.value;
    // };
    // const decodedContent = decodeHTML(value);
    setContent(unescape(value));
  }, [value]);

  //console.log("content: ", content?.toString());
  // const remainingCharacters = useMemo(
  //   () =>
  //     reactQuillRef?.current
  //       ? maxLength -
  //         (reactQuillRef?.current?.getEditor()?.getText().replace(/\n/g, "")
  //           .length || 0)
  //       : 0,
  //   [content, maxLength, reactQuillRef]
  // );

  const trimDeltaToMaxLength = (delta, maxLength) => {
    let trimmedDelta = { ops: [] };
    let currentLength = 0;

    for (const op of delta.ops) {
      if (op.insert) {
        const insertText = typeof op.insert === "string" ? op.insert : ""; // we ignore embeds like images here
        const remaining = maxLength - currentLength;

        if (insertText.length <= remaining) {
          // If whole insert fits, keep as-is
          trimmedDelta.ops.push(op);
          currentLength += insertText.length;
        } else {
          // Trim insert and keep its formatting
          if (remaining > 0) {
            trimmedDelta.ops.push({
              insert: insertText.slice(0, remaining),
              ...(op.attributes && { attributes: op.attributes }),
            });
            currentLength += remaining;
          }
          break; // done trimming
        }
      } else {
        // Other op types (retain, delete), skip in this context
      }
    }

    return new Delta(trimmedDelta);
  };

  useEffect(() => {
    if (reactQuillRef.current) {
      const editor = reactQuillRef.current.getEditor();
      const clipboard = editor.getModule("clipboard") as Quill["clipboard"]; // Type casting properly
      clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
        const currentText = editor.getText().replace(/\n/g, "");
        const remainingChars = maxLength - currentText.length;

        if (remainingChars <= 0) {
          return new Delta();
        }

        let charCount = 0;
        const newDelta = new Delta();

        for (const op of delta.ops) {
          if (typeof op.insert === "string") {
            const available = remainingChars - charCount;
            if (available <= 0) break;

            const sliced = op.insert.slice(0, available);
            newDelta.insert(sliced, op.attributes);
            charCount += sliced.length;
          } else {
            newDelta.insert(op.insert, op.attributes);
          }
        }

        return newDelta;
      });
      editor.on("text-change", (delta, oldDelta, source) => {
        const text = editor.getText().replace(/\n/g, ""); // Get plain text without new lines
        const length = text.length;

        // Check if the new length exceeds maxLength
        if (length > maxLength) {
          // Rollback the last change
          editor.deleteText(maxLength, length);
        }

        setRemainingCharacters(Math.max(0, maxLength - length));
      });

      return () => {
        editor.off("text-change");
      };
    }
  }, [maxLength]); // Run only once or if maxLength changes

  return (
    <>
      <div className={``}>
        <ReactQuill
          theme={"snow"}
          // onKeyDown={handleOnKeyDown} // Block input on key down
          ref={reactQuillRef}
          onChange={handleOnChange}
          value={content}
          modules={Editor.modules}
          formats={Editor.formats}
          bounds={".app"}
          placeholder={placeholder}
          readOnly={isDisabled}
          className={`custom-quill placeholder:text-[16px] 
         `}
        />

        {touched && error && <p className="text-sm text-red-500">{error}</p>}
      </div>
      {maxLength && (
        <p className="text-sm text-gray-500 mt-2">
          {remainingCharacters} characters left
        </p>
      )}
    </>
  );
};

Editor.modules = {
  toolbar: [
    [
      { header: "1" },
      { header: "2" },
      // { font: [] }
    ],
    // [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    // ["link", "image", "video"],
    ["clean"],
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  },
};

Editor.formats = [
  "header",
  // "font",
  // "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  // "link",
  // "image",
  // "video",
];

export default Editor;
